import { useState } from 'react';
import PdfPreview from './PdfPreview';
import { useTranslation } from 'react-i18next';
import CardStack, { type StackCard } from '../components/CardStack';
import ProviderSelector from './ProviderSelector';
import ShowNotesSection from './ShowNotesSection';
import type { StagePlotListItem } from '../api/stageplots';
import { stagePlotToSvg } from './StagePlotEditor/StagePlotCanvas';
import { resolveBacklineProvider, backlineOverrideFor, isVenueForcedTransport } from '../lib/backlineProvider';

// Role vocabulary for per-show overrides (same tags as musicians/crew cards).
const SHOW_ROLE_OPTIONS = [
  'manager', 'tour manager', 'foh', 'mon', 'lights', 'driver', 'stagehand', 'video', 'guest', 'other',
  'guitar tech', 'drum tech', 'keys tech',
];

// Stage plot thumbnail: render the PNG/JPEG when the plot is an uploaded image,
// else rasterize the vector plot to an SVG data URL so you can see what you select.
function stagePlotThumb(plot: StagePlotListItem | undefined): string | null {
  if (!plot) return null;
  if (plot.data.kind === 'image' && plot.data.imageUrl) return plot.data.imageUrl;
  try {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(stagePlotToSvg(plot.data as any))}`;
  } catch {
    return null;
  }
}

export interface TechTabProps {
  rider: any;
  transport: string;
  changeTransport: (t: string) => void;
  stagePlots: StagePlotListItem[];
  selectedStagePlotId: string | null;
  setSelectedStagePlotId: (id: string | null) => void;
  musicians: any[];
  selMus: Set<string>;
  toggleMus: (id: string) => void;
  crew: any[];
  selCrew: Set<string>;
  toggleCrew: (id: string) => void;
  createRider: () => void;
  realId: string;
  /** The show's band — feeds the ShowNotesSection capability-aware write gate. */
  bandId?: string | null;
  updateRiderSel: () => void;
  /** Same "Update musicians & crew" as the show card's People block — the Tech
      tab mirrors that selector, so it carries the same action (2026-10-23). */
  updatePeopleOnShow: () => void;
  regeneratingPeople: boolean;
  openPdf: () => void;
  /** Download the rider PDF (same document as the preview) — one click,
      no need to open the preview first. (2026-10-23) */
  downloadPdf: () => void;
  generating: boolean;
  deleteRider: () => void;
  showRoles: Record<string, string[]>;
  resetShowRoles: (memberId: string) => void;
  roleSaving: boolean;
  toggleShowRole: (memberId: string, role: string) => void;
  setBacklineProvider: (itemId: string, provider: 'band' | 'venue' | null) => void;
  backlineProviders: Record<string, 'band' | 'venue'>;
  pdfDoc: any;
}

// The tab is split into SEPARATE cards (same card-per-section pattern as the
// Account page, 2026-10-23): rider assembly, backline providers, per-show
// roles, tech notes — instead of one long card divided by border rules.
export default function TechTab(props: TechTabProps) {
  const { t } = useTranslation();
  const {
    rider, transport, changeTransport, stagePlots, selectedStagePlotId, setSelectedStagePlotId,
    musicians, selMus, toggleMus, crew, selCrew, toggleCrew, createRider, realId, bandId,
    updateRiderSel, openPdf, downloadPdf, updatePeopleOnShow, regeneratingPeople, generating, deleteRider, showRoles, resetShowRoles,
    roleSaving, toggleShowRole, setBacklineProvider, backlineProviders, pdfDoc,
  } = props;

  // Roles for this show live directly in the card (LOT D3): the card itself is
  // titled "Roles for this show" and is the ONLY fold — an inner
  // CollapsibleSection repeated the same title on the same card.
  const hasRoleOverrides = Object.keys(showRoles).length > 0;

  const renderStagePlotPicker = () => (
    stagePlots.length > 0 ? (
      <div className="form-field" style={{ marginBottom: 12 }}>
        <label>{t('techTab.stagePlot')}</label>
        <select className="form-input" value={selectedStagePlotId ?? ''} onChange={(e) => setSelectedStagePlotId(e.target.value || null)}>
          {stagePlots.map((plot) => <option key={plot.id} value={plot.id}>{plot.name}</option>)}
        </select>
        {(() => {
          const thumb = stagePlotThumb(stagePlots.find((p) => p.id === selectedStagePlotId));
          return thumb ? (
            <img src={thumb} alt={t('techTab.stagePlotPreview')} style={{ width: '100%', maxHeight: 160, objectFit: 'contain', marginTop: 8, border: '1px solid var(--border-light)', borderRadius: 6, background: '#fff' }} />
          ) : null;
        })()}
      </div>
    ) : null
  );

  const renderMusicianList = () => (
    <div className="form-field" style={{ marginBottom: 12 }}>
      {/* Same "Update musicians & crew" as the People block (own line, LEFT
          aligned — user 2026-10-23). Only meaningful once a rider exists. */}
      {rider && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            data-update-people
            disabled={regeneratingPeople}
            title={t('showDetail.updatePeopleTitle')}
            onClick={updatePeopleOnShow}
          >
            {regeneratingPeople ? t('showDetail.updatingPeople') : t('showDetail.updatePeopleOnShow')}
          </button>
        </div>
      )}
      <label>{t('techTab.musicians', { count: musicians.length })}</label>
      <div className="musician-inline-list">
        {musicians.length === 0 ? (
          <span style={{ color: 'var(--text-dim)' }}>{t('techTab.noMusicians')}</span>
        ) : musicians.map((m) => (
          <label key={m.id} className="musician-checkbox">
            <input type="checkbox" checked={selMus.has(m.id)} onChange={() => toggleMus(m.id)} />
            {m.name}
          </label>
        ))}
      </div>
    </div>
  );

  const renderCrewList = () => (
    <div className="form-field" style={{ marginBottom: 12 }}>
      <label>{t('techTab.crew', { count: crew.length })}</label>
      <div className="musician-inline-list">
        {crew.length === 0 ? (
          <span style={{ color: 'var(--text-dim)' }}>{t('techTab.noCrew')}</span>
        ) : crew.map((c) => (
          <label key={c.id} className="musician-checkbox">
            <input type="checkbox" checked={selCrew.has(c.id)} onChange={() => toggleCrew(c.id)} />
            {c.name}
          </label>
        ))}
      </div>
    </div>
  );



  // Has this show's selection any backline item to tune? (feeds card hasData)
  const selectedMembers = [
    ...musicians.filter((m) => selMus.has(m.id)),
    ...crew.filter((c) => selCrew.has(c.id)),
  ];
  const hasBacklineItems = selectedMembers.some(
    (m) => (Array.isArray(m.backline) ? m.backline : []).length > 0,
  );
  const [notesHasData, setNotesHasData] = useState(false);

  const TAB_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): `hasData` = the card really has something to show,
    // so a full card opens itself on a fresh view while an empty one stays
    // folded. `defaultOpen` is gone for that reason.
    { id: 'rider', hasData: !!rider, title: t('techTab.techRider'), node: (<div data-tech-tab>
  {!rider ? (
    <div>
      <p style={{ color: 'var(--text-dim)', marginBottom: 12 }}>
        {t('techTab.noRiderYet')}
      </p>
      <div className="form-field" style={{ marginBottom: 12 }}>
        <label>{t('techTab.transport')}</label>
        <select className="form-input" value={transport}
          onChange={(e) => changeTransport(e.target.value)}>
          <option value="van">{t('techTab.van')}</option>
          <option value="plane">{t('techTab.plane')}</option>
          <option value="tourbus">{t('techTab.tourBus')}</option>
          <option value="train">{t('techTab.train')}</option>
        </select>
      </div>
      {renderStagePlotPicker()}
      {renderMusicianList()}
      {renderCrewList()}
      <button className="btn btn-primary" onClick={createRider}
        disabled={!realId || selMus.size === 0 && selCrew.size === 0}>
        {t('techTab.generateRider')}
      </button>
    </div>
  ) : (
    <div>
      <p style={{ marginBottom: 12 }}>
        <strong>{rider.name}</strong> · {rider.transport === 'plane' ? 'Plane' : rider.transport === 'tourbus' ? 'Tour Bus' : rider.transport === 'train' ? 'Train' : ''}
        {rider.musician_names ? ` · ${rider.musician_names}` : ''}
      </p>
      <div className="form-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
          <label>{t('techTab.transport')}</label>
          <select className="form-input" value={transport}
            onChange={(e) => changeTransport(e.target.value)}>
            <option value="" disabled>{t('techTab.selectTransport')}</option>
            <option value="van">{t('techTab.van')}</option>
            <option value="plane">{t('techTab.plane')}</option>
            <option value="tourbus">{t('techTab.tourBus')}</option>
            <option value="train">{t('techTab.train')}</option>
          </select>
        </div>
      </div>
      {renderStagePlotPicker()}
      {renderMusicianList()}
      {renderCrewList()}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={updateRiderSel}>{t('techTab.saveChanges')}</button>
        <button className="btn btn-secondary" onClick={openPdf} disabled={generating}>
          {generating ? t('techTab.generating') : t('techTab.previewRiderPdf')}
        </button>
        {/* Download the rider PDF — same document as the preview, without
            opening it first (user 2026-10-23). */}
        <button className="btn btn-secondary" onClick={downloadPdf} disabled={generating}>
          {t('techTab.downloadPdf')}
        </button>
        <button className="btn btn-secondary" onClick={deleteRider}>{t('techTab.detach')}</button>
      </div>

      {pdfDoc && (
        <div style={{ marginTop: 12 }}>
          <PdfPreview docDef={pdfDoc} height={480} />
        </div>
      )}
    </div>
  )}
</div>) },
    { id: 'providers', hasData: hasBacklineItems, title: `🎸 ${t('techTab.backlineProviders')}`, sub: t('techTab.backlineProvidersHint'), node: (<div data-tech-backline-providers>
  {(() => {
    const members = [
      ...musicians.filter((m) => selMus.has(m.id)),
      ...crew.filter((c) => selCrew.has(c.id)),
    ];
    const withBackline = members
      .map((m) => ({
        member: m,
        // MIC STANDS included (2026-10-23, user): the show's final band/venue
        // choice applies to them too, like any backline item. (The rider PDF
        // keeps printing stands in the MIC STANDS section — this list is the
        // per-show provider editor, not the PDF's backline table.)
        items: (Array.isArray(m.backline) ? m.backline : []).filter(() => true),
      }))
      .filter((m) => m.items.length > 0);
    if (withBackline.length === 0) {
      return <p style={{ color: 'var(--text-dim)' }}>{t('techTab.selectMembersForBackline')}</p>;
    }
    return withBackline.map(({ member, items }) => (
      <div key={member.id} style={{ marginBottom: 14 }}>
        <strong style={{ fontSize: 'var(--font-sm)' }}>{member.name}</strong>
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((item: any) => {
            const override = backlineOverrideFor({ backlineProviders }, item.id);
            const effective = resolveBacklineProvider(item, transport, override);
            const forced = isVenueForcedTransport(transport);
            const hasOverride = override === 'band' || override === 'venue';
            const source = hasOverride
              ? t('techTab.customForShow')
              : (forced ? t('techTab.venueAutomatic') : t('techTab.fromCard'));
            const desc = item.label ? `${item.type || t('techTab.item')} — ${item.label}` : (item.type || t('techTab.item'));
            return (
              <div key={item.id} style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontSize: 'var(--font-sm)' }}>
                    <strong>{item.quantity ?? 1}×</strong> {desc}
                  </span>
                  {hasOverride && (
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => setBacklineProvider(item.id, null)}>
                      {t('techTab.resetToDefault')}
                    </button>
                  )}
                </div>
                <ProviderSelector value={effective} onChange={(v) => setBacklineProvider(item.id, v)} />
                <small style={{ color: 'var(--text-dim)' }}>{source}</small>
              </div>
            );
          })}
        </div>
      </div>
    ));
  })()}
</div>) },
    { id: 'roles', hasData: hasRoleOverrides, title: t('techTab.rolesForShow'), node: (<div data-tech-roles>
    <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 10px' }}>
      {t('techTab.rolesHint')}
    </p>
    {[...musicians.filter((m) => selMus.has(m.id)), ...crew.filter((c) => selCrew.has(c.id))].map((member) => {
      const bandTags = (member as any).tags?.length ? (member as any).tags : ((member as any).role ? [(member as any).role] : []);
      const overridden = showRoles[member.id] !== undefined;
      const active = overridden ? showRoles[member.id] : bandTags;
      return (
        <div key={member.id} style={{ marginBottom: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong style={{ fontSize: 'var(--font-sm)' }}>{member.name}</strong>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={roleSaving || !overridden}
              onClick={() => resetShowRoles(member.id)}
              title={t('techTab.backToDefaultsTitle')}
            >
              {t('techTab.resetToBandDefault')}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SHOW_ROLE_OPTIONS.map((opt) => {
              const isActive = active.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={roleSaving}
                  onClick={() => toggleShowRole(member.id, opt)}
                  className={`rc-pill${isActive ? ' on' : ''}`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {overridden && (
            <small style={{ color: 'var(--text-dim)' }}>
              {t('techTab.customShowDefault', { roles: bandTags.length ? bandTags.join(', ') : t('techTab.none') })}
            </small>
          )}
        </div>
      );
    })}
</div>) },
    { id: 'notes', hasData: notesHasData, title: t('showNotes.tech.title'), node: (<div data-tech-notes>
  <ShowNotesSection realId={realId || null} bandId={bandId} kind="tech" onHasDataChange={setNotesHasData} hideHeading />
</div>) },
  ];

  return (
    <div data-tech-tab>
      <CardStack pageId="show_tech" cards={TAB_CARDS} foldable />
      {/* Card 1 — rider assembly + actions + preview */}

      {/* Card 2 — backline providers (band vs venue, per item) */}

      {/* Card 3 — per-show roles, CLOSED by default (extras are the exception);
          it self-opens when a member carries an override. */}

      {/* Card 4 — show tech notes (a separate daysheet section, TECH Notes) */}
    </div>
  );
}
